import { t, type I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { Box, Stack } from '@ardoq/layout';
import { Skeleton, SkeletonLoader } from '@ardoq/loader';
import { ModalLayout, ModalSize, ModalTemplate } from '@ardoq/modal';
import { WarningNotification } from '@ardoq/status-ui';
import { Paragraph } from '@ardoq/typography';
import type { BulkDeleteComponentCount } from '../../types';
import {
  bulkDeleteOperations,
  type BulkDeleteCount,
} from './bulkDeleteOperations';
import {
  useBulkDeleteComponentCount,
  type GetBulkDeleteComponentCount,
} from './useBulkDeleteComponentCount';

export type BulkDeleteComponentsDialogProps = {
  getBulkDeleteComponentCount: GetBulkDeleteComponentCount;
  i18nLocale: I18nLocale;
  locale: Locale;
  onCancel: VoidFunction;
  onConfirm: (counts: BulkDeleteComponentCount) => Promise<void>;
};

const getTitle = (deletableCount: number | null, i18nLocale: I18nLocale) => {
  if (deletableCount === null || deletableCount === 0) {
    return t(
      i18nLocale,
      'inventory:bulkDelete.titleUnknownCount',
      'Delete components?'
    );
  }
  if (deletableCount === 1) {
    return t(
      i18nLocale,
      'inventory:bulkDelete.titleSingular',
      'Delete one component?'
    );
  }
  return t(
    i18nLocale,
    'inventory:bulkDelete.title',
    { componentCount: deletableCount },
    `Delete ${deletableCount} components?`
  );
};

const getSubmitButtonText = (
  deletableCount: number | null,
  i18nLocale: I18nLocale
) => {
  if (deletableCount === null || deletableCount === 0) {
    return t(
      i18nLocale,
      'inventory:bulkDelete.submitUnknownCount',
      'Delete components'
    );
  }
  return t(
    i18nLocale,
    'inventory:bulkDelete.submit',
    { componentCount: deletableCount },
    `Delete ${deletableCount}`
  );
};

const formatTypeNames = (componentTypeNames: string[], locale: Locale) =>
  new Intl.ListFormat(locale.code, {
    style: 'long',
    type: 'conjunction',
  }).format(componentTypeNames.map(name => `“${name}”`));

const getDescription = (
  deletableCount: number,
  componentTypeNames: string[],
  workspaceCount: number,
  locale: Locale,
  i18nLocale: I18nLocale
) => {
  const isAcrossWorkspaces = deletableCount > 1 && workspaceCount > 1;

  if (componentTypeNames.length === 0) {
    if (deletableCount === 1) {
      return t(
        i18nLocale,
        'inventory:bulkDelete.descriptionSingular',
        'This deletes one component, along with its references.'
      );
    }
    return isAcrossWorkspaces
      ? t(
          i18nLocale,
          'inventory:bulkDelete.descriptionPluralAcrossWorkspaces',
          { componentCount: deletableCount, workspaceCount },
          `This deletes ${deletableCount} components across ${workspaceCount} workspaces, along with their references.`
        )
      : t(
          i18nLocale,
          'inventory:bulkDelete.descriptionPlural',
          { componentCount: deletableCount },
          `This deletes ${deletableCount} components, along with their references.`
        );
  }

  const typeNames = formatTypeNames(componentTypeNames, locale);

  if (deletableCount === 1) {
    return t(
      i18nLocale,
      'inventory:bulkDelete.descriptionTypedSingular',
      { typeNames },
      `This deletes one ${typeNames} component, along with its references.`
    );
  }
  return isAcrossWorkspaces
    ? t(
        i18nLocale,
        'inventory:bulkDelete.descriptionTypedPluralAcrossWorkspaces',
        { componentCount: deletableCount, typeNames, workspaceCount },
        `This deletes ${deletableCount} ${typeNames} components across ${workspaceCount} workspaces, along with their references.`
      )
    : t(
        i18nLocale,
        'inventory:bulkDelete.descriptionTypedPlural',
        { componentCount: deletableCount, typeNames },
        `This deletes ${deletableCount} ${typeNames} components, along with their references.`
      );
};

const getBlockedWarning = (
  blockedCount: number,
  totalCount: number,
  deletableCount: number,
  i18nLocale: I18nLocale
) => {
  if (blockedCount === 0) {
    return null;
  }
  if (deletableCount === 0) {
    return t(
      i18nLocale,
      'inventory:bulkDelete.nothingDeletable',
      { totalCount },
      `None of the ${totalCount} components can be deleted — you don’t have edit access.`
    );
  }
  if (blockedCount === 1) {
    return t(
      i18nLocale,
      'inventory:bulkDelete.blockedWarningSingular',
      { totalCount },
      `One of the ${totalCount} components can’t be deleted — you don’t have edit access. It’ll be left untouched.`
    );
  }
  return t(
    i18nLocale,
    'inventory:bulkDelete.blockedWarningPlural',
    { blockedCount, totalCount },
    `${blockedCount} of the ${totalCount} components can’t be deleted — you don’t have edit access. They’ll be left untouched.`
  );
};

const BulkDeleteSummary = ({
  count,
  locale,
  i18nLocale,
}: {
  count: BulkDeleteCount;
  locale: Locale;
  i18nLocale: I18nLocale;
}) => {
  // Already announced by ModalTemplate's ErrorNotification (role="alert").
  if (count.status === 'error') {
    return null;
  }
  if (count.status === 'loading') {
    return (
      <SkeletonLoader
        message={t(
          i18nLocale,
          'inventory:bulkDelete.countLoading',
          'Counting the components this would delete'
        )}
      >
        <Skeleton width="80%" />
      </SkeletonLoader>
    );
  }

  const { deletable, total, componentTypeNames, workspaceCount } = count.counts;
  const blockedCount = bulkDeleteOperations.getBlockedCount(count) ?? 0;
  const blockedWarning = getBlockedWarning(
    blockedCount,
    total,
    deletable,
    i18nLocale
  );

  return (
    <Stack gap="medium">
      {deletable > 0 && (
        <Paragraph variant="text2">
          {getDescription(
            deletable,
            componentTypeNames,
            workspaceCount,
            locale,
            i18nLocale
          )}
        </Paragraph>
      )}
      {blockedWarning && (
        <WarningNotification>{blockedWarning}</WarningNotification>
      )}
    </Stack>
  );
};

export const BulkDeleteComponentsDialog = ({
  getBulkDeleteComponentCount,
  i18nLocale,
  locale,
  onCancel,
  onConfirm,
}: BulkDeleteComponentsDialogProps) => {
  const count = useBulkDeleteComponentCount(getBulkDeleteComponentCount);
  const deletableCount = bulkDeleteOperations.getDeletableCount(count);

  return (
    <ModalTemplate
      modalSize={ModalSize.S}
      headerText={getTitle(deletableCount, i18nLocale)}
      onCloseButtonClick={onCancel}
      closeButtonLabel={t(i18nLocale, 'inventory:bulkDelete.close', 'Close')}
      secondaryButtonText={t(
        i18nLocale,
        'inventory:confirmDialogs.cancel',
        'Cancel'
      )}
      onSecondaryButtonClick={onCancel}
      dangerButtonText={getSubmitButtonText(deletableCount, i18nLocale)}
      isDangerButtonDisabled={!bulkDeleteOperations.isSubmittable(count)}
      onDangerButtonClick={() =>
        count.status === 'loaded' ? onConfirm(count.counts) : undefined
      }
      errorBlockMessage={
        count.status === 'error'
          ? t(
              i18nLocale,
              'inventory:bulkDelete.countError',
              'We couldn’t count the components this would delete. Close the dialog and try again.'
            )
          : undefined
      }
    >
      <ModalLayout>
        {/* The counts arrive after the dialog opens and rewrite everything it
            says (WCAG 4.1.3). The region stays mounted and only its contents
            swap — one added together with its text is unreliably announced. */}
        <Box role="status" aria-live="polite" aria-atomic="true">
          <BulkDeleteSummary
            count={count}
            locale={locale}
            i18nLocale={i18nLocale}
          />
        </Box>
      </ModalLayout>
    </ModalTemplate>
  );
};
